import moment from 'moment-timezone';

import type { PatientChatQuestionRange } from '@mummssoftware/utils';

import { MAX_RANGE_DAYS } from '../constants';
import type { MaiaDateRange } from '../types';

const API_DATE = 'YYYY-MM-DD';

const startOfDay = (date: Date) => moment(date).startOf('day');

/** Last `days` days ending today, at day granularity. */
export const lastDaysRange = (
  days: number,
  now: Date = new Date(),
): MaiaDateRange => {
  const today = startOfDay(now);
  return {
    fromDate: today.clone().subtract(days, 'days').toDate(),
    toDate: today.toDate(),
  };
};

/**
 * What a new chat covers without the start-date picker: the last
 * MAX_RANGE_DAYS days ending today, starting at the referral date when the
 * patient was referred more recently. A referral date after today is ignored.
 */
export const defaultDateRange = (
  referral: Date | null = null,
  now: Date = new Date(),
): MaiaDateRange => {
  const range = lastDaysRange(MAX_RANGE_DAYS, now);
  if (!referral || Number.isNaN(referral.getTime())) {
    return range;
  }
  const from = startOfDay(referral);
  return from.isAfter(range.fromDate) && !from.isAfter(range.toDate)
    ? { ...range, fromDate: from.toDate() }
    : range;
};

/** A chat always covers MAX_RANGE_DAYS days; the user only picks where they start. */
export const rangeFromStart = (start: Date): MaiaDateRange => {
  const from = startOfDay(start);
  return {
    fromDate: from.toDate(),
    toDate: from.clone().add(MAX_RANGE_DAYS, 'days').toDate(),
  };
};

/** Earliest and latest start date the picker allows. */
export type StartDateBounds = { minDate: Date; maxDate: Date };

/**
 * The start-date picker shows only for a patient referred more than
 * MAX_RANGE_DAYS days ago: from the referral date up to today - MAX_RANGE_DAYS,
 * so the window never runs past today. Otherwise (recent or no referral date)
 * null: the chat covers the last MAX_RANGE_DAYS days.
 */
export const startDateBounds = (
  referral: Date | null,
  now: Date = new Date(),
): StartDateBounds | null => {
  if (!referral || Number.isNaN(referral.getTime())) {
    return null;
  }
  const min = startOfDay(referral);
  const max = startOfDay(now).subtract(MAX_RANGE_DAYS, 'days');
  return min.isBefore(max)
    ? { minDate: min.toDate(), maxDate: max.toDate() }
    : null;
};

/** Typed input can bypass the calendar's min/max, so Apply and + check again. */
export const isStartDateAllowed = (
  start: Date,
  { minDate, maxDate }: StartDateBounds,
): boolean => {
  const day = startOfDay(start);
  return (
    day.isValid() &&
    !day.isBefore(startOfDay(minDate)) &&
    !day.isAfter(startOfDay(maxDate))
  );
};

export const toApiDate = (date: Date): string => moment(date).format(API_DATE);

/**
 * MAIA Chats' date filter as the gateway takes it: from the start of the
 * `from` day to the start of the day after `to`, both days in the site's zone
 * (the days the grid shows), as ISO instants.
 */
export const questionRange = (
  from: Date,
  to: Date,
  siteTimezone?: string | null,
): PatientChatQuestionRange => {
  const siteDay = (date: Date) =>
    siteTimezone
      ? moment.tz(toApiDate(date), API_DATE, siteTimezone)
      : moment(toApiDate(date), API_DATE);
  return {
    from: siteDay(from).toISOString(),
    to: siteDay(to).add(1, 'day').toISOString(),
  };
};

/** Strict YYYY-MM-DD parse; null for anything else (missing, malformed, time-stamped). */
export const fromApiDate = (value: string | null | undefined): Date | null => {
  if (!value) {
    return null;
  }
  const parsed = moment(value, API_DATE, true);
  return parsed.isValid() ? parsed.toDate() : null;
};

/** Range for the lock label of a past conversation, when the gateway stored one. */
export const rangeFromApi = (
  fromDate: string | null | undefined,
  toDate: string | null | undefined,
): MaiaDateRange | null => {
  const from = fromApiDate(fromDate);
  const to = fromApiDate(toDate);
  return from && to ? { fromDate: from, toDate: to } : null;
};

/**
 * The patient's referral date: epoch ms (patient header) or YYYY-MM-DD.
 * null when missing or unreadable.
 */
export const parseReferralDate = (
  referralDate: number | string | null | undefined,
): Date | null => {
  if (typeof referralDate === 'string') {
    return fromApiDate(referralDate);
  }
  if (!referralDate) {
    return null;
  }
  const date = new Date(referralDate);
  return Number.isNaN(date.getTime()) ? null : date;
};
